'use client';
import React, { useState } from 'react';
import { t } from 'i18next';
import { TimelineProps } from '../types/leaveRequestType';
import UserShortCut from 'components/common/UserShortCut';
import Tippy from '@tippyjs/react';
import moment from 'moment';
import TruncateText from 'components/common/TruncateText';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import UpdateApproval from 'features/settings/approval/approval/components/UpdateApproval';
import { useAuthContext } from 'contexts/AuthContext';
import { hasPermission } from 'utils/permissions';

const Timeline: React.FC<TimelineProps & { ApprovalPermission?: boolean }> = ({
  createBy,
  approval,
  approvalId,
  refetch,
}) => {
  const { openSidebar } = useSidebarManager();
  const { user } = useAuthContext();
  const ApprovalPermission =
    hasPermission('Settings.Edit.Approval') ||
    user.role === 'Proprietaire' ||
    user.role === 'SupportMintHR';
  const getStatusClass = (status: string): string => {
    switch (status) {
      case 'Accepter':
        return 'minthr-bg-success';
      case 'En_Cour':
        return 'minthr-bg-warning';
      case 'Holiday_Canceled':
        return 'minthr-bg-danger';
      case 'Refuser':
        return 'minthr-bg-danger';
      default:
        return '';
    }
  };

  const getIcon = (status: string): string => {
    switch (status) {
      case 'Accepter':
        return '/icons/check.svg';
      case 'En_Cour':
        return '/icons/waiting.svg';
      case 'Holiday_Canceled':
        return '/icons/close.svg';
      case 'Refuser':
        return '/icons/close.svg';
      default:
        return '';
    }
  };
  const formatDate = (date: Date, status: string | null): string => {
    if (!date) return '';
    if (
      (status !== 'Accepter' &&
        status !== 'Refuser' &&
        status !== 'Holiday_Canceled') ||
      !status
    ) {
      return moment(date).format('DD MMM YYYY');
    } else {
      return moment(date).format('DD MMM YYYY HH:mm');
    }
  };

  const [hovered, setHovered] = useState(false);

  return (
    <>
      <div
        className="minthr-border minthr-border-rounded minthr-p-3 minthr-pl-0 minthr-mb-4"
        style={{ position: 'relative' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {hovered && ApprovalPermission && (
          <Tippy content={t('Approval.OpenSidebar')}>
            <button
              type="button"
              onClick={() =>
                openSidebar(UpdateApproval, false, {
                  id: Number(approvalId),
                  type: 'User',
                  refetch,
                  Sidebartype: 'Secondary',
                })
              }
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                padding: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
              }}
            >
              <img src="/icons/eye.svg" alt="view" width={18} height={18} />
            </button>
          </Tippy>
        )}
        <div className="minthr-timeline minthr-timeline-collapse minthr-overflow-auto open">
          <div className="minthr-timeline-item minthr-pb-2">
            <div className="minthr-timeline-date-icon minthr-icon-bg minthr-icon-bg-xxs minthr-icon-circle minthr-bg-info-opacity minthr-ml-1">
              <img
                className="minthr-icon-info minthr-icon-xs"
                src="/icons/plus.svg"
                alt="plus icon"
                width={16}
                height={16}
              />
            </div>
            <div className="minthr-timeline-content minthr-p-2 minthr-bg-light-gray minthr-icon-rounded">
              <div className="minthr-flex minthr-flex-items-center minthr-flex-space-between">
                <UserShortCut
                  userId={createBy?.id}
                  userPath={createBy?.image}
                  alt={createBy?.fullName}
                  className="minthr-avatar minthr-avatar-s"
                />
                <div className="minthr-ml-2" style={{ width: '50%' }}>
                  <div className="h5 text-s minthr-text-truncate minthr-mb-0">
                    {createBy?.fullName}
                  </div>
                  <p className="text-s minthr-mb-0">
                    {t(createBy?.fonctionName)}
                  </p>
                </div>
                <div className="minthr-ml-3 w-100 minthr-text-align-right">
                  <span className="minthr-icon-text">
                    <img
                      className="minthr-icon-xs minthr-ml-auto"
                      src="/icons/calendar.svg"
                      alt="calendar icon"
                      width={16}
                      height={16}
                    />
                    {moment(createBy?.date).format('DD MMM YYYY HH:mm') ?? ''}
                  </span>
                  <div className="minthr-flex minthr-flex-items-center">
                    <div className="progress progress-bar-s w-100">
                      <div
                        className="progress-bar minthr-progress-bg-info"
                        role="progressbar"
                        style={{ width: `${createBy?.approvelTaux | 0}%` }}
                      ></div>
                    </div>
                    <span className="minthr-ml-2 text-s">
                      {createBy?.approvelDone}/{createBy?.approvelCount}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {approval &&
            approval.map((item) => (
              <div key={item?.id} className="minthr-timeline-item minthr-pb-2">
                <div
                  className={`minthr-timeline-date-icon minthr-icon-bg minthr-icon-bg-xxs minthr-icon-circle ${getStatusClass(item?.status)} minthr-ml-1`}
                >
                  <img
                    className="minthr-icon-xxs minthr-icon-white"
                    src={getIcon(item?.status)}
                    alt={item?.status}
                    width={16}
                    height={16}
                  />
                </div>
                <div className="minthr-timeline-content minthr-p-2 minthr-bg-light-gray minthr-icon-rounded">
                  <div className="minthr-flex minthr-flex-items-center minthr-flex-space-between">
                    <UserShortCut
                      userId={item?.id}
                      userPath={item?.image}
                      alt={item?.fullName}
                      className="minthr-avatar minthr-avatar-s"
                    />
                    <div className="minthr-ml-2" style={{ width: '50%' }}>
                      <div className="h5 text-s minthr-text-truncate minthr-mb-0">
                        {item?.fullName}
                      </div>
                      <p className="text-s minthr-mb-0">
                        {t(item?.fonctionName)}
                      </p>
                    </div>
                    <div className="minthr-ml-3 w-100 minthr-text-align-right">
                      <span className="minthr-icon-text">
                        {item?.date && (
                          <>
                            <img
                              className="minthr-icon-xs minthr-ml-auto"
                              src="/icons/calendar.svg"
                              alt="calendar icon"
                              width={16}
                              height={16}
                            />
                            {formatDate(item?.date, item?.status)}
                          </>
                        )}
                      </span>
                      <div className="minthr-flex minthr-flex-items-center">
                        {item?.raison ? (
                          <Tippy content={item?.raison.slice(0, 10)}>
                            <span
                              className={`text-s minthr-badge-dot minthr-ml-auto minthr-px-0 minthr-p-0 minthr-hover-none minthr-badge-dot-${item?.status == 'Accepter' ? 'success' : item?.status == 'En_Cour' ? 'warning' : 'danger'}`}
                            >
                              {t('Status.' + item?.status)}
                            </span>
                          </Tippy>
                        ) : (
                          <span
                            className={`text-s minthr-badge-dot minthr-ml-auto minthr-px-0 minthr-p-0 minthr-hover-none minthr-badge-dot-${item?.status == 'Accepter' ? 'success' : item?.status == 'En_Cour' ? 'warning' : 'danger'}`}
                          >
                            {t('Status.' + item?.status)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {item?.raison && (
                    <div className="minthr-flex-items-start minthr-text minthr-pt-2">
                      {/* <div className="minthr-color-gray text-s">
                        {t('Raison')} : {item?.raison}
                      </div> */}
                      <TruncateText
                        tag="div"
                        className="minthr-color-gray text-s"
                        showExpandButton
                        buttonClassName="minthr-expand-long-text minthr-color-primary minthr-text-decoration-underline readMoreLink text-s"
                      >
                        {t('Raison')} : {item?.raison}
                      </TruncateText>
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>
    </>
  );
};

export default Timeline;
